import { Button, Flex } from 'antd';
import { useCallback, useState } from 'react';
import { UploadRequestOption } from 'rc-upload/es/interface';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from '../translations/AlgorithmsUpload.translations.json';
import { useFormContext } from 'react-hook-form';
import { DataObjectFormDataType } from '../types';
import { useMutateAlgorithmsUpload } from '../query/useMutateAlgorithmsUpload';
import {
  DropdownUpload,
  Modal,
  Padding,
  Tooltip,
} from '../../common/components';
import { OnProgressHandler } from './types';
import { useNavigate } from 'react-router-dom';
import { ApiErrorResponse } from '../../common/ApiClient';

const { addTranslations, useTranslation } = i18nNsFactory('algorithms-upload');
addTranslations(translations);

export interface AlgorithmsUploadProps {
  id?: number;
  onError: (error: ApiErrorResponse) => void;
  onProgress?: OnProgressHandler;
  isInput?: boolean;
}

export function AlgorithmsUpload({
  id,
  onError,
  onProgress,
  isInput,
}: AlgorithmsUploadProps) {
  const { t } = useTranslation();
  const {
    formState: { isDirty },
  } = useFormContext<DataObjectFormDataType>();
  const { mutate } = useMutateAlgorithmsUpload(onProgress);
  const navigate = useNavigate();

  const refreshPage = useCallback(() => {
    const { pathname, search } = window.location;
    const newPath = pathname.replace('/edit', '');
    const searchParams = new URLSearchParams(search);
    searchParams.delete('algorithmId');
    navigate(
      {
        pathname: newPath,
        search: searchParams.toString(),
      },
      { state: { saved: 'success', noBlocker: true } },
    );
    Modal.destroyAll();
  }, [navigate]);

  const handleError = useCallback(
    (error: ApiErrorResponse) => {
      Modal.destroyAll();
      onError?.(error);
    },
    [onError],
  );

  const handleUpload = useCallback(
    (request: UploadRequestOption) => {
      const upload = (replace: boolean) => {
        mutate(
          { file: request.file, id, replace },
          {
            onError: handleError,
            onSuccess: refreshPage,
          },
        );
      };

      Modal.confirm({
        title: t('algorithms.upload-confirmation'),
        content: t(
          isDirty ? 'algorithms.upload-dirty' : 'algorithms.upload-unmodified',
        ),
        footer: <UploadModalFooter mutate={upload} />,
      });
    },
    [id, isDirty, mutate, handleError, refreshPage, t],
  );

  return (
    <DropdownUpload
      id="dataObject-upload"
      customRequest={handleUpload}
      disabled={!id}
      showUploadList={false}
    >
      <Tooltip
        title={
          id ? undefined : t(`disabled.unsaved.${isInput ? 'input' : 'output'}`)
        }
      >
        {t('upload.algorithms')}
      </Tooltip>
    </DropdownUpload>
  );
}

function UploadModalFooter({ mutate }: { mutate: (replace: boolean) => void }) {
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);

  return (
    <Padding top>
      <Flex justify="end" gap={8}>
        <Button
          id="cancel-algorithms-upload"
          disabled={disabled}
          type="default"
          onClick={Modal.destroyAll}
        >
          {t('button.cancel')}
        </Button>
        <Button
          id="merge-uploaded-algorithms"
          disabled={disabled}
          type="primary"
          onClick={() => {
            mutate(false);
            setDisabled(true);
          }}
        >
          {t('button.modify')}
        </Button>
        <Button
          id="replace-uploaded-algorithms"
          disabled={disabled}
          type="primary"
          onClick={() => {
            mutate(true);
            setDisabled(true);
          }}
        >
          {t('button.replace')}
        </Button>
      </Flex>
    </Padding>
  );
}
